// Guaranteed Payment Exposure page

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Button, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// MUI Date Picker
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// React
import { useEffect } from "react";

import { useReportData } from "store/ReportDataContext";
import GuaranteedExposureCard from "./GuaranteedExposureCard";

const formatLastLoaded = (timestamp) =>
  timestamp ? dayjs(timestamp).format("MMM D, YYYY h:mm A") : "never";

const formatRangeDate = (date) => (date ? dayjs(date).format("YYYY-MM-DD") : "—");

function Exposure() {
  const {
    issuedAfter,
    setIssuedAfter,
    issuedBefore,
    setIssuedBefore,
    lastLoadedAt,
    handleSearch,
    ensureLoaded,
    billedBills,
    mattersOriginData,
    activityKeyedData,
    pKeyeddata,
    loadingBills,
    loadingMatters,
    loadingActivities,
    loadingpersistedMatters,
    loadingpersistedPayments,
  } = useReportData();

  // No-op when the Data page already loaded everything; runs the full load when
  // this page is opened directly or after a browser reload.
  useEffect(() => {
    ensureLoaded();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox px={10} pt={2} pb={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Issued After"
                        value={issuedAfter}
                        onChange={(newValue) => setIssuedAfter(newValue)}
                        renderInput={(params) => <TextField fullWidth {...params} />}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Issued Before"
                        value={issuedBefore}
                        onChange={(newValue) => setIssuedBefore(newValue)}
                        renderInput={(params) => <TextField fullWidth {...params} />}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSearch}
                        startIcon={<SearchIcon />}
                        sx={{
                          backgroundColor: "#1976d2",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "#1565c0",
                          },
                        }}
                      >
                        Search
                      </Button>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
                <MDBox pt={2}>
                  <MDTypography variant="caption" color="text">
                    {`Last loaded: ${formatLastLoaded(lastLoadedAt)}`}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <GuaranteedExposureCard
              rows={billedBills}
              mattersRows={mattersOriginData}
              activityKeyedData={activityKeyedData}
              pKeyeddata={pKeyeddata}
              loading={
                loadingBills ||
                loadingMatters ||
                loadingActivities ||
                loadingpersistedMatters ||
                loadingpersistedPayments
              }
              dateRangeLabel={`Issued ${formatRangeDate(issuedAfter)} to ${formatRangeDate(
                issuedBefore
              )}`}
            />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Exposure;
